import { Box, styled } from '@mui/material';
import ErrorBoundary from 'components/ErrorBoundary';
import { Column } from 'further-ui/components/Layout';
import { revaluation as rules } from 'further-ui/validations';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Vehicle } from 'further-types/lp/vehicle';
import useRevaluationFormSync from '../../hooks/useRevaluationFormSync';
import processRevaluationHistory from '../../../helpers/processRevaluationHistory';
import getLatestRevaluationsForEachHoldingClass from '../../helpers/getLatestRevaluationsForEachHoldingClass';
import FormWrapper from '../../FormWrapper';
import HoldingClassTransactionAllocator from '../../ReviewRevaluation/HoldingClassTransactionAllocator';

type Props = {
  companyId: string;
  assetId: string;
  vehicle: Vehicle;
  savedData: rules.RevaluationFormType;
  onDataChange: (companyId: string, data: rules.RevaluationFormType) => void;
};

const PaddedContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const CompanyReviewSection: React.FC<Props> = ({
  companyId,
  assetId,
  vehicle,
  savedData,
  onDataChange,
}) => {
  const formMethods = useForm<rules.RevaluationFormType>({
    defaultValues: savedData,
  });

  const { transactionTypeOptions, isLoading: isLoadingTransactionTypes } =
    useTransactionTypeSelectorOptions();

  const globalFxRate =
    formMethods.watch('revaluationCurrencyRates.global') ?? null;

  useRevaluationFormSync(
    formMethods,
    savedData,
    (data) => onDataChange(companyId, data),
    { skipReset: true },
  );

  const allRevaluations = [
    ...(savedData.revaluations ?? []),
    ...(savedData.newRevaluations ?? []).filter((r) => !r.isRemoved),
  ];

  const revaluationHistory = processRevaluationHistory(allRevaluations);

  const latestRevaluationsWithChanges =
    getLatestRevaluationsForEachHoldingClass(revaluationHistory).filter(
      (rev) =>
        rev.isNew &&
        (rev.hasChange ||
          (rev.currency !== vehicle.baseCurrency && !!globalFxRate)),
    );

  return (
    <FormWrapper
      formMethods={formMethods}
      externalState={{ selectedVehicle: vehicle, assetFullHistory: {} }}
    >
      <PaddedContent>
        <Column spacing="lg">
          {!isLoadingTransactionTypes &&
            transactionTypeOptions.length > 0 &&
            latestRevaluationsWithChanges.map((rev, index) => (
              <ErrorBoundary key={rev.holdingClassId}>
                <HoldingClassTransactionAllocator
                  assetId={assetId}
                  revaluation={rev}
                  holdingClassIndex={index}
                  transactionTypeOptions={transactionTypeOptions}
                  vehicle={vehicle}
                  globalFxRate={globalFxRate}
                  isFetchingRate={false}
                  shouldHideValuationChangeAmount={true}
                />
              </ErrorBoundary>
            ))}
        </Column>
      </PaddedContent>
    </FormWrapper>
  );
};

export default CompanyReviewSection;
